// AdminAnalytics.jsx — Analytics v2 (overnight 2026-07-11).
// Time-bucketed reporting (day/week/month/year period picker, default =
// current week) + portal-vs-QuickBooks reconciliation ("Channels" tab):
// the QB "Sales by Item" report import is the offline-sales source of truth
// (see qbSalesService.js). Cancelled orders are excluded from all stats.
import { useEffect, useState } from "react";
import { db } from "../config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getAllProducts, getQbSales } from "../services/dataCache";
import { parseQbSalesCsv, uploadQbSales, qbQtyBySkuForRange, qbDataThrough } from "../services/qbSalesService";
import { PERIODS, periodRange, formatRange } from "../utils/periods";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";

export default function AdminAnalytics() {
  const { user, profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [qbMonths, setQbMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("channels"); // channels | top | bottom | neversold | customers | reps
  const [period, setPeriod] = useState("thisWeek");

  // QB report import (admin only)
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  useEffect(() => {
    async function load() {
      const [orderSnap, cachedProducts, userSnap, qbDocs] = await Promise.all([
        getDocs(collection(db, "orders")),
        getAllProducts(),
        getDocs(collection(db, "users")),
        getQbSales()
      ]);
      setOrders(orderSnap.docs.map(d => d.data()));
      setProducts(cachedProducts);
      setUsers(userSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
      setQbMonths(qbDocs);
      setLoading(false);
    }
    load();
  }, []);

  async function handleImport() {
    if (!importFile || importing) return;
    setImporting(true);
    setImportMsg("");
    try {
      const text = await importFile.text();
      const { months, stats } = parseQbSalesCsv(text);
      if (stats.rowsUsed === 0) {
        setImportMsg("No sales rows found — is this the QB 'Sales by Item' report export?");
        return;
      }
      const ok = window.confirm(
        `Import QB sales data?\n\n` +
        `${stats.rowsUsed} sale lines, ${stats.skuCount} products\n` +
        `${stats.firstDate} to ${stats.lastDate} (${stats.monthCount} month${stats.monthCount !== 1 ? "s" : ""})\n\n` +
        `Months already imported are REPLACED by this report (safe to re-import).`
      );
      if (!ok) return;
      const written = await uploadQbSales(months, user.uid);
      setQbMonths(await getQbSales(true));
      setImportMsg(`Imported ${stats.rowsUsed} sale lines across ${written} month${written !== 1 ? "s" : ""} ✓`);
      setImportFile(null);
    } catch (err) {
      setImportMsg(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }

  if (loading) return <p>Loading analytics...</p>;

  const range = periodRange(period);
  const [start, end] = range;

  // --- Portal orders in the selected period (cancelled excluded) ---
  const periodOrders = orders.filter(o => {
    if (o.status === "cancelled") return false;
    const t = o.createdAt?.toDate?.();
    return t && t >= start && t < end;
  });

  // --- Aggregate portal quantity sold per SKU ---
  const salesBySku = {};
  for (const order of periodOrders) {
    for (const item of order.items || []) {
      if (!salesBySku[item.sku]) salesBySku[item.sku] = { qty: 0, revenue: 0 };
      salesBySku[item.sku].qty += item.qty;
      salesBySku[item.sku].revenue += item.qty * item.priceAtOrder;
    }
  }

  const productMap = Object.fromEntries(products.map(p => [p.sku, p]));

  const soldProducts = Object.entries(salesBySku).map(([sku, data]) => ({
    sku,
    name: productMap[sku]?.name || sku,
    qty: data.qty,
    revenue: data.revenue
  }));

  const topSellers = [...soldProducts].sort((a, b) => b.qty - a.qty).slice(0, 25);
  const bottomSellers = [...soldProducts].sort((a, b) => a.qty - b.qty).slice(0, 25);

  // Active products with zero sales in the period
  const soldSkus = new Set(Object.keys(salesBySku));
  const neverSold = products
    .filter(p => p.active && !soldSkus.has(p.sku))
    .slice(0, 25);

  // --- Channels: portal vs QuickBooks (offline) reconciliation ---
  const qbBySku = qbQtyBySkuForRange(qbMonths, start, end);
  const allSkus = new Set([...Object.keys(salesBySku), ...Object.keys(qbBySku)]);
  const channelRows = [...allSkus].map(sku => {
    const portalQty = salesBySku[sku]?.qty || 0;
    const qbQty = qbBySku[sku] || 0;
    // QB is the ground truth for total movement; portal is the floor when the
    // QB report hasn't been refreshed since those portal orders synced.
    const totalQty = Math.max(qbQty, portalQty);
    const offlineQty = Math.max(0, qbQty - portalQty);
    return { sku, name: productMap[sku]?.name || sku, portalQty, qbQty, offlineQty, totalQty };
  }).filter(r => r.totalQty > 0).sort((a, b) => b.totalQty - a.totalQty);

  const totals = channelRows.reduce((acc, r) => ({
    total: acc.total + r.totalQty,
    portal: acc.portal + r.portalQty,
    offline: acc.offline + r.offlineQty
  }), { total: 0, portal: 0, offline: 0 });
  const portalRevenue = periodOrders.reduce((s, o) => s + (o.totalAmount || 0), 0);
  const dataThrough = qbDataThrough(qbMonths);

  // --- Aggregate by customer / by rep (period-scoped) ---
  const userMap = Object.fromEntries(users.map(u => [u.uid, u]));
  const byCustomer = {};
  for (const order of periodOrders) {
    const cid = order.customerId;
    if (!byCustomer[cid]) byCustomer[cid] = { orders: 0, revenue: 0 };
    byCustomer[cid].orders += 1;
    byCustomer[cid].revenue += order.totalAmount || 0;
  }
  const customerRows = Object.entries(byCustomer)
    .map(([uid, data]) => ({
      name: userMap[uid]?.companyName || userMap[uid]?.name || "Unknown",
      ...data
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const byRep = {};
  for (const order of periodOrders) {
    const placer = userMap[order.placedBy];
    if (!placer || placer.role !== "rep") continue;
    if (!byRep[order.placedBy]) byRep[order.placedBy] = { orders: 0, revenue: 0 };
    byRep[order.placedBy].orders += 1;
    byRep[order.placedBy].revenue += order.totalAmount || 0;
  }
  const repRows = Object.entries(byRep)
    .map(([uid, data]) => ({ name: userMap[uid]?.name || "Unknown", ...data }))
    .sort((a, b) => b.revenue - a.revenue);

  const chartData =
    view === "top" ? topSellers.map(p => ({ label: p.sku, qty: p.qty, name: p.name })) :
    view === "bottom" ? bottomSellers.map(p => ({ label: p.sku, qty: p.qty, name: p.name })) :
    [];

  return (
    <div className="admin-analytics">
      <h2>Sales Analytics</h2>

      <div className="analytics-period-row">
        {PERIODS.map(p => (
          <button
            key={p.key}
            className={`view-chip${period === p.key ? " active" : ""}`}
            onClick={() => setPeriod(p.key)}
          >{p.label}</button>
        ))}
        <span className="period-range-label">{formatRange(range)}</span>
      </div>

      <div className="analytics-tabs">
        <button className={view === "channels" ? "active" : ""} onClick={() => setView("channels")}>Portal vs QuickBooks</button>
        <button className={view === "top" ? "active" : ""} onClick={() => setView("top")}>Top Sellers</button>
        <button className={view === "bottom" ? "active" : ""} onClick={() => setView("bottom")}>Bottom Sellers</button>
        <button className={view === "neversold" ? "active" : ""} onClick={() => setView("neversold")}>Not Sold</button>
        <button className={view === "customers" ? "active" : ""} onClick={() => setView("customers")}>By Customer</button>
        <button className={view === "reps" ? "active" : ""} onClick={() => setView("reps")}>By Rep</button>
      </div>

      {view === "channels" && (
        <>
          <div className="stat-cards">
            <div className="stat-card"><span className="stat-value">{totals.total.toFixed(0)}</span><span className="stat-label">Total units sold</span></div>
            <div className="stat-card"><span className="stat-value">{totals.portal.toFixed(0)}</span><span className="stat-label">Portal units</span></div>
            <div className="stat-card"><span className="stat-value">{totals.offline.toFixed(0)}</span><span className="stat-label">QuickBooks (offline) units</span></div>
            <div className="stat-card"><span className="stat-value">${portalRevenue.toFixed(2)}</span><span className="stat-label">Portal revenue</span></div>
          </div>

          {qbMonths.length === 0 ? (
            <p className="inventory-help">
              No QuickBooks sales data imported yet — totals above show portal orders only.
              {isAdmin && " Import the QB \"Sales by Item\" report below to see true totals including phone/fax/in-person sales."}
            </p>
          ) : (
            <p className="inventory-help-note">
              QuickBooks data imported through <strong>{dataThrough || "—"}</strong>. Units sold in QB
              beyond portal orders are attributed to the QuickBooks (offline) channel.
            </p>
          )}

          {channelRows.length > 0 && (
            <table>
              <thead>
                <tr><th>SKU</th><th>Product</th><th>Total</th><th>Portal</th><th>QuickBooks</th></tr>
              </thead>
              <tbody>
                {channelRows.slice(0, 50).map(r => (
                  <tr key={r.sku}>
                    <td>{r.sku}</td>
                    <td>{r.name}</td>
                    <td><strong>{r.totalQty.toFixed(r.totalQty % 1 ? 2 : 0)}</strong></td>
                    <td>{r.portalQty.toFixed(r.portalQty % 1 ? 2 : 0)}</td>
                    <td>{r.offlineQty.toFixed(r.offlineQty % 1 ? 2 : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {isAdmin && (
            <div className="import-qb-box">
              <h3>Import QuickBooks sales report</h3>
              <p className="inventory-help-note">
                QuickBooks → Reports → Sales → <strong>Sales by Item Detail</strong> → set the date
                range → Export as CSV. Re-importing overlapping months is safe (they're replaced).
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              <button onClick={handleImport} disabled={!importFile || importing}>
                {importing ? "Importing..." : "Import QB Sales"}
              </button>
              {importMsg && <p className="inventory-success">{importMsg}</p>}
            </div>
          )}
        </>
      )}

      {(view === "top" || view === "bottom") && (
        <>
          {chartData.length === 0 ? (
            <p className="inventory-help">No portal sales in this period.</p>
          ) : (
            <>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="label" width={70} fontSize={11} />
                    <Tooltip
                      formatter={(value, key, props) => [value, props.payload.name]}
                    />
                    <Bar dataKey="qty">
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={view === "top" ? "#2f7a3f" : "#b00020"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table>
                <thead><tr><th>SKU</th><th>Product</th><th>Qty Sold</th><th>Revenue</th></tr></thead>
                <tbody>
                  {(view === "top" ? topSellers : bottomSellers).map(p => (
                    <tr key={p.sku}>
                      <td>{p.sku}</td><td>{p.name}</td><td>{p.qty}</td>
                      <td>${p.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {view === "neversold" && (
        <>
          <p className="inventory-help">
            Active products with zero portal orders in this period — candidates for a push or promotion.
          </p>
          <table>
            <thead><tr><th>SKU</th><th>Product</th><th>Stock on Hand</th></tr></thead>
            <tbody>
              {neverSold.map(p => (
                <tr key={p.sku}>
                  <td>{p.sku}</td><td>{p.name}</td><td>{Math.floor(p.stockQty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {view === "customers" && (
        customerRows.length === 0
          ? <p className="inventory-help">No portal orders in this period.</p>
          : (
            <table>
              <thead><tr><th>Customer</th><th>Orders</th><th>Total Revenue</th></tr></thead>
              <tbody>
                {customerRows.map((c, i) => (
                  <tr key={i}><td>{c.name}</td><td>{c.orders}</td><td>${c.revenue.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          )
      )}

      {view === "reps" && (
        repRows.length === 0
          ? <p>No rep-placed orders in this period.</p>
          : (
            <table>
              <thead><tr><th>Rep</th><th>Orders Placed</th><th>Total Revenue</th></tr></thead>
              <tbody>
                {repRows.map((r, i) => (
                  <tr key={i}><td>{r.name}</td><td>{r.orders}</td><td>${r.revenue.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          )
      )}
    </div>
  );
}
